import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  Skeleton,
  alpha,
  useTheme,
  Card,
  CardContent
} from '@mui/material';
import {
  Description as DocumentIcon,
  Lock as LockIcon,
  Archive as ArchiveIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  Source as SourceIcon,
  Pages as PagesIcon,
  Business as ExternalIcon,
  CheckCircle as ReceivedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LocalizedDate from '../common/LocalizedDate';
import { useTaskTranslations } from '../../utils/taskUtils';
import useRTL from '../../hooks/useRTL';

export const DocumentDisplay = ({ document, loading, error, task }) => {
  const theme = useTheme();
  const { t } = useTranslation(['documents', 'tasks']);
  const { getDocTypeLabel } = useTaskTranslations();
  const { isRTL, getRTLProps } = useRTL();

  // Helper function to get localized source type
  const getLocalizedSourceType = (sourceType) => {
    if (!sourceType) return '-';
    const sourceTypeMap = {
      'INTERNAL': t('documents:source_internal', 'Internal'),
      'EXTERNAL': t('documents:source_external', 'External'),
      'PROVINCIAL': t('documents:source_provincial', 'Provincial')
    };
    return sourceTypeMap[sourceType] || sourceType;
  };

  // Check if task is physically received (RECEIVED status)
  const isPhysicallyReceived = task?.status === 'RECEIVED';

  if (loading) {
    return (
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
        }} />
        <Box sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="80%" height={20} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Skeleton variant="text" width="100%" height={16} />
              </Grid>
              <Grid item xs={6}>
                <Skeleton variant="text" width="100%" height={16} />
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ mb: 3, p: 2.5, borderRadius: 2, borderLeft: 3, borderColor: 'error.main' }}>
        <Typography variant="body2" color="error">
          {t('documents:error_loading_document')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        background: theme.palette.background.paper,
        position: 'relative',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Decorative top gradient */}
      <Box sx={{ 
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
      }} />

      {/* Header Section */}
      <Box sx={{ 
        p: 2.5,
        pb: 2,
        background: alpha(theme.palette.primary.main, 0.02),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Stack direction="row" spacing={2.5} alignItems="flex-start" sx={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            flexShrink: 0
          }}>
            <DocumentIcon sx={{ fontSize: 28 }} />
          </Box>
          
          <Box flex={1} minWidth={0}>
            <Typography 
              variant="h6" 
              fontWeight={600}
              sx={{ 
                mb: 0.5,
                mr: isRTL ? 0 : 1,
                ml: isRTL ? 1 : 0,
                lineHeight: 1.3,
                wordBreak: 'break-word',
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              {document?.title || t('documents:untitled_document')}
            </Typography>
            
            {document?.description && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  mr: isRTL ? 0 : 1,
                  ml: isRTL ? 1 : 0,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.4,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {document.description}
              </Typography>
            )}
          </Box>
          
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
            {isPhysicallyReceived && (
              <Chip 
                size="small" 
                icon={<ReceivedIcon />}
                label={t('tasks:physically_received', 'Physically Received')} 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.7rem',
                  backgroundColor: '#81c784', // Light green (different from complete green #4caf50)
                  color: 'white',
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }}
              />
            )}
            {document?.is_confidential && (
              <Chip 
                size="small" 
                icon={<LockIcon />}
                label={t('documents:confidential')} 
                color="error"
                variant="filled"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {document?.is_archived && (
              <Chip 
                size="small" 
                icon={<ArchiveIcon />}
                label={t('documents:archived')} 
                color="info"
                variant="filled"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Four Key Attributes Section */}
      <Box sx={{ p: 2.5 }}>
        <Typography 
          variant="subtitle1" 
          fontWeight={600} 
          sx={{ 
            mb: 2,
            color: theme.palette.primary.main,
            textAlign: 'center'
          }}
        >
          {t('documents:document_information')}
        </Typography>
        
        <Grid container spacing={2}>
          {/* First Row - Core Document Information */}
          <Grid item xs={12} sm={6} md={2.4}>
            <DocumentAttributeCard
              icon={<AssignmentIcon />}
              label={t('documents:reference_number')}
              value={document?.reference_number || '-'}
              color="primary"
              theme={theme}
              isRTL={isRTL}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <DocumentAttributeCard
              icon={<CalendarIcon />}
              label={t('documents:document_date')}
              value={
                document?.document_date ? (
                  <LocalizedDate 
                    date={document.document_date} 
                    typographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                ) : '-'
              }
              color="success"
              theme={theme}
              isRTL={isRTL}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <DocumentAttributeCard
              icon={<CategoryIcon />}
              label={t('tasks:document_type')}
              value={document?.type || '-'}
              color="info"
              theme={theme}
              isRTL={isRTL}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <DocumentAttributeCard
              icon={<SourceIcon />}
              label={t('tasks:source_type', 'Source Type')}
              value={getLocalizedSourceType(document?.source_type)}
              color="warning"
              theme={theme}
              isRTL={isRTL}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <DocumentAttributeCard
              icon={<PagesIcon />}
              label={t('documents:number_of_pages', 'Pages')}
              value={document?.number_of_pages || '-'}
              color="info"
              theme={theme}
              isRTL={isRTL}
            />
          </Grid>

          {/* Second Row - External Source (only show if EXTERNAL) */}
          {document?.source_type === 'EXTERNAL' && document?.external_source_name && (
            <Grid item xs={12}>
              <DocumentAttributeCard
                icon={<ExternalIcon />}
                label={t('documents:external_source_name', 'External Source')}
                value={document.external_source_name}
              color="secondary"
              theme={theme}
              fullWidth={true}
              isRTL={isRTL}
            />
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  );
};

// Compact Document Attribute Card Component
const DocumentAttributeCard = ({ icon, label, value, color, theme, fullWidth = false, isRTL }) => {
  const colorMap = {
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    success: theme.palette.success,
    info: theme.palette.info,
    warning: theme.palette.warning,
    error: theme.palette.error
  };
  
  const selectedColor = colorMap[color] || colorMap.primary;

  return (
    <Card
      elevation={1}
      sx={{
        height: '100%',
        borderRadius: 2,
        background: alpha(selectedColor.main, 0.03),
        border: `1px solid ${alpha(selectedColor.main, 0.1)}`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(selectedColor.main, 0.1)}`,
          borderColor: alpha(selectedColor.main, 0.2)
        }
      }}
    >
      <CardContent sx={{ 
        p: fullWidth ? 2 : 1.5, 
        textAlign: fullWidth ? (isRTL ? 'right' : 'left') : 'center',
        display: fullWidth ? 'flex' : 'block',
        alignItems: fullWidth ? 'center' : 'normal',
        gap: fullWidth ? 2 : 0,
        flexDirection: fullWidth && isRTL ? 'row-reverse' : 'row'
      }}>
        {/* Icon */}
        <Box sx={{
          width: fullWidth ? 40 : 32,
          height: fullWidth ? 40 : 32,
          borderRadius: '50%',
          background: alpha(selectedColor.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: fullWidth ? 0 : '0 auto',
          mb: fullWidth ? 0 : 1,
          color: selectedColor.main,
          flexShrink: 0
        }}>
          {React.cloneElement(icon, { sx: { fontSize: fullWidth ? 20 : 16 } })}
        </Box>

        {/* Content Container for fullWidth */}
        <Box sx={{ 
          flex: fullWidth ? 1 : 'none',
          textAlign: fullWidth ? (isRTL ? 'right' : 'left') : 'center'
        }}>
          {/* Label */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontWeight: 600, 
              mb: fullWidth ? 0.25 : 0.5, 
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: fullWidth ? '0.7rem' : '0.65rem'
            }}
          >
            {label}
          </Typography>

          {/* Value */}
          <Typography 
            variant={fullWidth ? "body1" : "body2"} 
            fontWeight={600}
            sx={{ 
              color: selectedColor.main,
              wordBreak: 'break-word',
              lineHeight: 1.2,
              minHeight: fullWidth ? 24 : 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: fullWidth ? (isRTL ? 'flex-end' : 'flex-start') : 'center'
            }}
          >
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}; 